import { useCallback, useEffect, useState } from "react";
import { DatabaseBackup, ShieldCheck, Download, FlaskConical, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, downloadFile } from "@/lib/api";
import { inputClass } from "@/components/FieldInput";
import { PageHeader, Card, StatusBadge } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OffsiteCard } from "@/components/OffsiteCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const KIND = { daily: "Giornaliero", weekly: "Settimanale integrale", monthly: "Mensile", manual: "Manuale", pre_restore: "Pre-ripristino" };
const COLLECTIONS = ["trusts", "subjects", "trust_subjects", "assets", "accounting", "deadlines", "fiscal", "diary", "chart_of_accounts", "prospects", "documents", "users"];
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(2)} MB` : `${(b / 1024).toFixed(1)} KB`);

function RestoreDialog({ backup, onClose, onDone }) {
  const [f, setF] = useState({ scope: "collection", collection: "trusts", record_id: "", reason: "", confirm: "", code: "" });
  const [cid, setCid] = useState(null);
  const [dev, setDev] = useState(null);
  const [busy, setBusy] = useState(false);
  const sendOtp = async () => {
    try { const { data } = await api.post("/restore/otp"); setCid(data.challenge_id); setDev(data.dev_code || null); toast.success("Codice di conferma inviato alla tua email"); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/backups/${backup.id}/restore`, { ...f, record_id: f.record_id || null, collection: f.scope === "full" ? null : f.collection, challenge_id: cid });
      toast.success(`Ripristino completato in ${data.duration_s}s`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Ripristino dal backup del {fmtDateTime(backup.created_at)}</DialogTitle>
          <DialogDescription>TM-ISMS-07 §7: la richiesta viene registrata con evento, ambito e punto di ripristino. Prima del ripristino viene creato automaticamente un backup di sicurezza.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select className={inputClass} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="restore-scope-select">
            <option value="collection">Ripristino parziale (archivio o singolo record)</option>
            <option value="full">Ripristino completo dell'ambiente</option>
          </select>
          {f.scope === "collection" && (
            <div className="grid grid-cols-2 gap-2">
              <select className={inputClass} value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} data-testid="restore-collection-select">
                {COLLECTIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input className={inputClass} placeholder="ID record (facoltativo)" value={f.record_id} onChange={(e) => setF({ ...f, record_id: e.target.value })} data-testid="restore-record-input" />
            </div>
          )}
          {f.scope === "full" && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">Durante il ripristino completo gli accessi degli altri utenti vengono sospesi (modalità manutenzione). Tutti i dati attuali saranno sostituiti con quelli del backup; il log di audit non viene mai sovrascritto.</p>}
          <textarea className={`${inputClass} h-20 py-2`} placeholder="Evento che motiva il ripristino (obbligatorio)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="restore-reason-input" />
          <input className={inputClass} placeholder='Digita "RIPRISTINA" per confermare' value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="restore-confirm-input" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={sendOtp} data-testid="restore-send-otp">{cid ? "Reinvia codice" : "Invia codice OTP di conferma"}</Button>
            <input className={`${inputClass} font-mono tracking-widest`} maxLength={6} placeholder="Codice" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })} data-testid="restore-otp-input" />
          </div>
          {dev && <p className="text-xs text-amber-700" data-testid="restore-dev-code">Anteprima — codice: <b className="font-mono">{dev}</b></p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button disabled={busy || !cid || f.code.length !== 6 || f.confirm !== "RIPRISTINA"} onClick={run} className="bg-red-600 hover:bg-red-700 text-white" data-testid="restore-submit">{busy ? "Ripristino…" : "Esegui ripristino"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DrillReport({ r }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={`drill-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="font-medium text-sm">Verbale test di ripristino · {fmtDateTime(r.created_at)}</p>
        <StatusBadge value={r.outcome} />
      </div>
      <p className="text-xs text-slate-500 mb-3">Backup del {fmtDateTime(r.backup_date)} · eseguito da {r.performed_by} · durata {r.duration_s}s (RTO obiettivo {r.rto_target_h}h {r.rto_met ? "rispettato" : "NON rispettato"}) · età punto di ripristino {r.rpo_hours}h</p>
      <ul className="grid sm:grid-cols-2 gap-1">
        {r.checks.map((c, i) => (
          <li key={i} className="text-xs flex items-start gap-1.5"><span className={c.ok ? "text-emerald-600" : "text-red-600"}>{c.ok ? "✓" : "✗"}</span><span className="text-slate-600">{c.check}{c.records !== undefined ? ` (${c.records})` : ""}</span></li>
        ))}
      </ul>
    </div>
  );
}

export default function Backup() {
  const [d, setD] = useState(null);
  const [tests, setTests] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [restore, setRestore] = useState(null);
  const load = useCallback(() => {
    api.get("/backups").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/restore-tests").then((r) => setTests(r.data)).catch(() => {});
    api.get("/restore-requests").then((r) => setReqs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const res = await fn(); toast.success(typeof ok === "function" ? ok(res.data) : ok); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const p = d?.policy;

  return (
    <div>
      <PageHeader eyebrow="TM-ISMS-07 · A.8.13 · A.8.24" title="Backup & Ripristino" subtitle="Copie cifrate AES-256-GCM con checksum SHA-256, retention automatica, verifica di integrità, test di ripristino su istanza separata e ripristino controllato con doppia conferma OTP.">
        <Button disabled={busy === "new"} onClick={() => act("new", () => api.post("/backups"), "Backup completato e cifrato")} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="backup-run-button">
          <DatabaseBackup className="h-4 w-4 mr-1" />{busy === "new" ? "Backup in corso…" : "Esegui backup ora"}
        </Button>
      </PageHeader>

      {p && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[["RPO", `${p.rpo_hours} ore`], ["RTO", `${p.rto_hours} ore`], ["Cifratura", p.encryption], ["Impronta chiave", p.key_fingerprint]].map(([k, v]) => (
            <Card key={k} className="p-4"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</p><p className="font-mono text-sm mt-2 text-slate-900">{v}</p></Card>
          ))}
          <Card className="p-4 col-span-2 lg:col-span-4 text-xs text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
            <span><b>Pianificazione:</b> {p.schedule}</span>
            <span><b>Retention:</b> giornalieri 30 gg · settimanali 12 settimane · mensili 12 mesi</span>
            <span className="flex items-center gap-1"><Lock className="h-3 w-3" />Copie immutabili: nessun utente può cancellarle</span>
          </Card>
        </div>
      )}

      <OffsiteCard onCopied={load} />

      <Tabs defaultValue="register">
        <TabsList className="bg-white border border-slate-200 mb-6 h-auto flex-wrap">
          <TabsTrigger value="register" data-testid="backup-tab-register" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Registro dei Backup</TabsTrigger>
          <TabsTrigger value="tests" data-testid="backup-tab-tests" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Verbali test di ripristino</TabsTrigger>
          <TabsTrigger value="requests" data-testid="backup-tab-requests" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Richieste di ripristino</TabsTrigger>
        </TabsList>
        <TabsContent value="register">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="backups-table">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Contenuto</th><th className="px-4 py-3">Dimensione</th><th className="px-4 py-3">Verifica</th><th className="px-4 py-3">Copia esterna</th><th className="px-4 py-3">Scadenza</th><th className="px-4 py-3 text-right">Azioni</th></tr>
              </thead>
              <tbody>
                {d?.backups.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Nessun backup registrato</td></tr>}
                {d?.backups.map((b) => {
                  const m = b.manifest;
                  const records = m ? Object.values(m.collections).reduce((a, x) => a + x, 0) : 0;
                  const ok = b.status === "completato" || b.status === "scaduto";
                  return (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`backup-row-${b.id}`}>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDateTime(b.created_at)}<p className="text-slate-400">{b.created_by}</p></td>
                      <td className="px-4 py-3 text-xs">{KIND[b.kind] || b.kind}</td>
                      <td className="px-4 py-3"><StatusBadge value={b.status} />{b.error && <p className="text-[11px] text-red-600 mt-1 max-w-[200px] truncate">{b.error}</p>}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{m ? `${records} record · ${m.files} file` : "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.size_bytes ? size(b.size_bytes) : "—"}</td>
                      <td className="px-4 py-3 text-xs">{b.verified_at ? <span className={b.verify_ok ? "text-emerald-700" : "text-red-600"}>{b.verify_ok ? "Integro" : "Errore"} · {fmtDateTime(b.verified_at)}</span> : <span className="text-slate-400">Non verificato</span>}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`backup-offsite-${b.id}`}>{b.offsite ? <span className={b.offsite.verified ? "text-emerald-700" : "text-red-600"}>Drive · {fmtDateTime(b.offsite.at).split(",")[0]}</span> : <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(b.expires_at).split(",")[0]}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {ok && (
                          <>
                            <Button variant="ghost" size="icon" title="Verifica integrità" disabled={!!busy} data-testid={`backup-verify-${b.id}`} onClick={() => act(`v${b.id}`, () => api.post(`/backups/${b.id}/verify`), (r) => (r.ok ? "Backup integro: checksum e decifratura OK" : "Verifica fallita"))}><ShieldCheck className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" title="Test di ripristino" disabled={!!busy} data-testid={`backup-drill-${b.id}`} onClick={() => act(`d${b.id}`, () => api.post(`/backups/${b.id}/drill`), (r) => `Test di ripristino: ${r.outcome.replace("_", " ")}`)}><FlaskConical className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" title="Scarica copia cifrata (copia esterna)" data-testid={`backup-download-${b.id}`} onClick={() => downloadFile(`/backups/${b.id}/download`, `trustmaster_${b.created_at.slice(0, 10)}_${b.kind}.tmb`).then(load).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" title="Ripristina" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`backup-restore-${b.id}`} onClick={() => setRestore(b)}><RotateCcw className="h-4 w-4" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="tests">
          <div className="space-y-3" data-testid="drill-list">
            {tests.length === 0 && <Card className="p-10 text-center text-sm text-slate-400">Nessun test eseguito. Avvia un test con l'icona del matraccio nel Registro dei Backup (cadenza trimestrale).</Card>}
            {tests.map((t) => <DrillReport key={t.id} r={t} />)}
          </div>
        </TabsContent>
        <TabsContent value="requests">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="restore-requests-table">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Richiedente</th><th className="px-4 py-3">Ambito</th><th className="px-4 py-3">Motivo</th><th className="px-4 py-3">Esito</th><th className="px-4 py-3">Durata</th></tr>
              </thead>
              <tbody>
                {reqs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nessuna richiesta</td></tr>}
                {reqs.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5 text-xs">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-xs">{r.requested_by}</td>
                    <td className="px-4 py-2.5 text-xs">{r.scope === "full" ? "Completo" : `${r.collection}${r.record_id ? ` · ${r.record_id.slice(0, 8)}` : ""}`}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[260px] truncate">{r.reason}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={r.status} /></td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.duration_s ? `${r.duration_s}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
      {restore && <RestoreDialog backup={restore} onClose={() => setRestore(null)} onDone={() => { setRestore(null); load(); }} />}
    </div>
  );
}
